import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { NotificationContext } from '../../contexts/NotificationContext';

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHrs = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const NotificationDropdown = ({ anchorEl, open, onClose }) => {
  const navigate = useNavigate();
  const { notifications, markAsRead, markAllAsRead } = useContext(NotificationContext);

  const recentNotifications = notifications.slice(0, 10);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    onClose();
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: { width: 380, maxHeight: 480 },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Notifications
        </Typography>
        <Button size="small" onClick={handleMarkAllRead}>
          Mark all as read
        </Button>
      </Box>
      <Divider />

      {/* Notification list */}
      {recentNotifications.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No notifications yet
          </Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ maxHeight: 350, overflowY: 'auto' }}>
          {recentNotifications.map((notification) => (
            <ListItemButton
              key={notification._id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {!notification.isRead && (
                <CircleIcon
                  sx={{
                    fontSize: 8,
                    color: 'primary.main',
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
              )}
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={notification.isRead ? 400 : 600}
                    noWrap
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>
                      {formatTimeAgo(notification.createdAt)}
                    </Typography>
                  </Box>
                }
                sx={{ ml: notification.isRead ? 2.5 : 0 }}
              />
            </ListItemButton>
          ))}
        </List>
      )}

      <Divider />
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Button size="small" onClick={handleViewAll} fullWidth>
          View All Notifications
        </Button>
      </Box>
    </Popover>
  );
};

export default NotificationDropdown;
